import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { money, d, errMsg } from "@/api";
import { Badge, MarginTag, DIRECTIONS, DIRECTION_LABELS } from "@/components/Badge";
import { useAuth } from "@/context/AuthContext";

export default function Quotes() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/quotes").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const [editRow, setEditRow] = useState(null);

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/quotes/${editRow.id}`, {
        request_id: editRow.request_id, supplier_offer_id: editRow.supplier_offer_id || null,
        cost_amount: Number(editRow.cost_amount), cost_currency: editRow.cost_currency,
        direction: editRow.direction || "export",
        sale_amount: Number(editRow.sale_amount), sale_currency: editRow.sale_currency,
        valid_until: editRow.valid_until || null, fx_locked: !!editRow.fx_locked, notes: editRow.notes || "",
      });
      toast.success("Teklif güncellendi");
      setEditRow(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`${r.no} numaralı teklif kalıcı olarak silinecek. Devam edilsin mi?`)) return;
    try {
      await api.delete(`/quotes/${r.id}`);
      toast.success("Teklif silindi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const exportCsv = () => {
    const head = ["Teklif No", "Talep No", "Müşteri", "Rota", "Maliyet TRY", "Satış TRY", "Kâr TRY", "Marj %", "Durum"];
    const lines = rows.map((r) => [r.no, r.request_no, r.customer_name, r.route, r.cost_try, r.sale_try, r.profit_try, r.margin_pct, r.status]);
    const csv = [head, ...lines].map((l) => l.map((x) => `"${x}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = "teklifler.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline">Fiyatlama</p>
          <h1 className="font-display text-4xl font-black tracking-tighter">Teklifler</h1>
        </div>
        <button className="brut-btn-ghost" onClick={exportCsv} data-testid="export-quotes-button">CSV Dışa Aktar</button>
      </div>

      {editRow && (
        <form onSubmit={saveEdit} className="brut animate-fade-up p-4 sm:p-6" data-testid="quote-edit-form">
          <p className="overline">{editRow.no} · Teklifi Düzenle</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-5">
            <div>
              <label className="overline">İşlem Tipi</label>
              <select className="brut-input mt-1.5" value={editRow.direction || "export"}
                onChange={(e) => setEditRow({ ...editRow, direction: e.target.value })} data-testid="quote-edit-direction-select">
                {DIRECTIONS.map((x) => <option key={x} value={x}>{DIRECTION_LABELS[x]}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Maliyet ({editRow.cost_currency})</label>
              <input required type="number" step="0.01" className="brut-input mt-1.5" value={editRow.cost_amount}
                onChange={(e) => setEditRow({ ...editRow, cost_amount: e.target.value })} data-testid="quote-edit-cost-input" />
            </div>
            <div>
              <label className="overline">Satış ({editRow.sale_currency})</label>
              <input required type="number" step="0.01" className="brut-input mt-1.5" value={editRow.sale_amount}
                onChange={(e) => setEditRow({ ...editRow, sale_amount: e.target.value })} data-testid="quote-edit-sale-input" />
            </div>
            <div>
              <label className="overline">Geçerlilik</label>
              <input type="date" className="brut-input mt-1.5" value={editRow.valid_until || ""}
                onChange={(e) => setEditRow({ ...editRow, valid_until: e.target.value })} data-testid="quote-edit-validuntil-input" />
            </div>
            <div className="flex items-end">
              <button className="brut-btn w-full" data-testid="quote-edit-save">Kaydet</button>
            </div>
            <div className="flex items-end">
              <button type="button" className="brut-btn-ghost w-full" onClick={() => setEditRow(null)} data-testid="quote-edit-cancel">Vazgeç</button>
            </div>
          </div>
        </form>
      )}
      <div className="brut overflow-x-auto" data-testid="quotes-table">
        <table className="dense w-full">
          <thead>
            <tr><th>Teklif No</th><th>Talep</th><th>Müşteri</th><th>İşlem</th><th>Rota</th><th className="!text-right">Maliyet</th>
              <th className="!text-right">Satış</th><th className="!text-right">Kâr</th><th className="!text-right">Marj</th>
              <th>Geçerlilik</th><th>Durum</th><th className="!text-right">İşlem</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`quote-list-row-${r.id}`}>
                <td className="font-mono font-semibold">{r.no}</td>
                <td>
                  <Link to={`/talepler/${r.request_id}`} className="font-mono text-klein hover:underline">{r.request_no}</Link>
                </td>
                <td>{r.customer_name}</td>
                <td className="text-xs uppercase" data-testid={`quote-direction-${r.id}`}>{DIRECTION_LABELS[r.direction] || "Export"}</td>
                <td className="text-xs">{r.route}</td>
                <td className="tnum text-right font-mono">{money(r.cost_try)}</td>
                <td className="tnum text-right font-mono">{money(r.sale_try)}</td>
                <td className="tnum text-right font-mono">{money(r.profit_try)}</td>
                <td className="text-right"><MarginTag pct={r.margin_pct} /></td>
                <td className="text-xs">{d(r.valid_until)}</td>
                <td><Badge value={r.status} /></td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-3">
                    {can("quote:write") && r.status !== "onaylandi" && (
                      <button onClick={() => setEditRow({ ...r, valid_until: (r.valid_until || "").slice(0, 10) })}
                        className="text-zinc-400 hover:text-klein" data-testid={`edit-quote-${r.id}`} title="Düzenle">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {can("*") && (
                      <button onClick={() => remove(r)} className="text-zinc-400 hover:text-red-600"
                        data-testid={`delete-quote-${r.id}`} title="Kalıcı olarak sil">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={12} className="py-10 text-center text-zinc-500">Henüz teklif yok.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
